import { useEffect } from 'react'

import {
  Button,
  CurrencyField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  HumanizedSelectField,
  NumberField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  ACCOUNT_TYPES,
  type AccountTypes,
  CATEGORY_OPTIONS,
  getCategoryDetailOptions,
  PAYMENT_CHANNELS_OPTIONS,
  type TransactionDTO,
  type UpdateCheckDTO,
  useProjectMutating,
  useProjectMutations,
} from '@/modules/projects'
import type { Check, Transaction } from '@/modules/projects/stores'
import {
  useDialogActions,
  useDialogData,
  useDialogIsOpen,
  useForm,
} from '@/stores'
import { humanizeText } from '@/utils'

const dialogId = 'edit-transaction'

// A check row from lukita is serialized with `typename: "CheckRecord"`. The
// militao TransactionsTable feeds both regular transactions and checks into
// the same dialog, so we branch on typename rather than wiring a parallel
// dialog and route.
const isCheck = (row: Transaction | Check): row is Check =>
  row?.typename === 'CheckRecord'

export const EditTransactionDialog = () => {
  const { successToast, errorToast } = useToast()
  const open = useDialogIsOpen(dialogId)
  const transaction = useDialogData() as Transaction | Check
  const { closeDialog } = useDialogActions()
  const { data, errors, setFieldValue } = useForm()
  const { updateTransaction, updateCheck } = useProjectMutations()
  const isUpdating = useProjectMutating()
  const editingCheck = transaction && isCheck(transaction)
  const noun = editingCheck ? 'Check' : 'Transaction'

  const categoryDetailOptions = getCategoryDetailOptions(
    data.category as string,
  )

  useEffect(() => {
    if (!transaction || !open) return
    Object.keys(transaction).forEach((field) =>
      setFieldValue(field, transaction[field as keyof Transaction] as string),
    )
  }, [open, transaction])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (editingCheck) {
      const body: Partial<UpdateCheckDTO> = {
        amount: data.amount as unknown as number,
        description: data.description as string,
        date: data.date as string,
        account_number: data.accountNumber as string,
        institution: data.institution as string,
        account_owner: data.accountOwner as string,
        check_number: data.checkNumber as unknown as number | null,
      }

      updateCheck(transaction.id, body)
        .then(() => closeDialog(true))
        .then(() => successToast('Updated check successfully'))
        .catch((error) => errorToast('Failed to update check', error))
      return
    }

    const {
      categoryDetail,
      paymentChannel,
      accountNumber,
      accountType,
      accountOwner,
      ...common
    } = data as Partial<Transaction>

    updateTransaction(transaction.id, {
      ...common,
      category_detail: categoryDetail as string,
      account_number: accountNumber as string,
      account_type: accountType as AccountTypes,
      payment_channel: paymentChannel as string,
      account_owner: accountOwner as string,
    } as TransactionDTO)
      .then(() => closeDialog(true))
      .then(() => successToast('Updated transaction successfully'))
      .catch((error) => errorToast('Failed to update transaction', error))
  }

  const handleCategoryChange = (value: string) => {
    const newOptions = getCategoryDetailOptions(value)
    setFieldValue('category', value)
    setFieldValue('categoryDetail', humanizeText(newOptions[0]))
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {noun}</DialogTitle>
          <DialogDescription>
            Update the {noun.toLowerCase()} details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label="Date"
                  type="date"
                  value={data?.date as string}
                  error={errors?.date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('date', e.target.value)
                  }
                />
                <CurrencyField
                  label="Amount"
                  value={data?.amount as string}
                  error={errors?.amount}
                  onChange={(value) => setFieldValue('amount', value)}
                />
              </div>
              <TextField
                label="Description"
                value={data?.description as string}
                error={errors?.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('description', e.target.value)
                }
              />
              <div className="grid grid-cols-2 gap-4">
                <NumberField
                  label="Account"
                  value={data?.accountNumber as string}
                  error={errors?.accountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('accountNumber', e.target.value)
                  }
                  maxLength={4}
                  helpText="Exact 4 last numbers"
                />
                {editingCheck ? (
                  <NumberField
                    label="Check Number"
                    value={data?.checkNumber as string}
                    error={errors?.checkNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('checkNumber', e.target.value)
                    }
                  />
                ) : (
                  <HumanizedSelectField
                    label="Account Type"
                    placeholder="Select an account type"
                    value={`${data?.accountType}`}
                    error={errors?.accountType}
                    required
                    onValueChange={(value: string) => {
                      setFieldValue('accountType', value)
                    }}
                    options={ACCOUNT_TYPES as unknown as string[]}
                  />
                )}
              </div>
              {!editingCheck && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <TextField
                      label="Merchant"
                      value={data?.merchant as string}
                      error={errors?.merchant}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('merchant', e.target.value)
                      }
                    />
                    <HumanizedSelectField
                      label="Payment Channel"
                      value={data?.paymentChannel as string}
                      error={errors?.paymentChannel}
                      onValueChange={(value: string) => {
                        setFieldValue('paymentChannel', value)
                      }}
                      required
                      options={PAYMENT_CHANNELS_OPTIONS}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <HumanizedSelectField
                      label="Category"
                      value={data?.category as string}
                      error={errors?.category}
                      onValueChange={handleCategoryChange}
                      required
                      options={CATEGORY_OPTIONS}
                    />
                    <HumanizedSelectField
                      key={data?.category as string}
                      label="Category Detail"
                      required
                      value={data?.categoryDetail as string}
                      error={errors?.categoryDetail}
                      options={categoryDetailOptions as string[]}
                      onValueChange={(value: string) => {
                        setFieldValue('categoryDetail', value)
                      }}
                    />
                  </div>
                </>
              )}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label="Institution"
                  value={data?.institution as string}
                  error={errors?.institution}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('institution', e.target.value)
                  }
                />
                <TextField
                  label="Account Owner"
                  // Null when no source named an owner; an unset value would make this input
                  // uncontrolled.
                  value={(data?.accountOwner as string) ?? ''}
                  error={errors?.accountOwner}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('accountOwner', e.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isUpdating}>
              Edit {noun}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

EditTransactionDialog.id = dialogId
